import { CATEGORIES, CATEGORY_LABELS, type Category } from '@helpdesk/shared';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, ChevronRight, Pencil, Plus, Search } from 'lucide-react';
import { useDeferredValue } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { BackButton } from '../components/ui/back.tsx';
import { ButtonLink } from '../components/ui/button.tsx';
import { Input, Select } from '../components/ui/form.tsx';
import { Alert, Card, EmptyState, PageHeader, Spinner } from '../components/ui/misc.tsx';
import { api, ApiError, qs } from '../lib/api.ts';
import { formatDate } from '../lib/format.ts';
import { usePageTitle } from '../lib/navigation.ts';
import { useSession } from '../lib/session.ts';
import type { KbArticle } from '../lib/types.ts';
import { NotFoundPage } from './NotFoundPage.tsx';

export function HelpPage() {
  const { isIt } = useSession();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const category = (params.get('category') ?? '') as Category | '';
  const deferredQ = useDeferredValue(q);

  const query = useQuery({
    queryKey: ['kb', 'list', deferredQ, category],
    queryFn: () => api.get<{ items: KbArticle[] }>(`/api/kb${qs({ q: deferredQ.trim(), category })}`),
    placeholderData: (prev) => prev,
  });

  const update = (next: Record<string, string>) => {
    const merged = { q, category, ...next };
    setParams(Object.fromEntries(Object.entries(merged).filter(([, v]) => v)), { replace: true });
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={isIt ? 'Knowledge base' : 'Help articles'}
        description={
          isIt
            ? 'Published articles are self-help for staff and suggested when resolving tickets. Drafts are IT-only.'
            : 'Quick fixes for common problems — try these before reporting a ticket.'
        }
        actions={
          isIt && (
            <ButtonLink to="/help/new">
              <Plus className="size-4" aria-hidden />
              New article
            </ButtonLink>
          )
        }
      />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
            aria-hidden
          />
          <Input
            type="search"
            aria-label="Search help articles"
            placeholder="Search, e.g. “printer”, “Wi-Fi password”"
            value={q}
            onChange={(e) => update({ q: e.target.value })}
            className="pl-9"
          />
        </div>
        <Select
          aria-label="Problem type"
          options={CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))}
          placeholder="All problem types"
          value={category}
          onChange={(e) => update({ category: e.target.value })}
          className="sm:w-56"
        />
      </div>

      {query.isPending && <Spinner />}
      {query.isError && <Alert tone="error">{query.error.message}</Alert>}
      {query.data && query.data.items.length === 0 && (
        <Card>
          <EmptyState
            icon={<BookOpen className="size-6" />}
            title={q || category ? 'No matching articles' : 'No help articles yet'}
            action={<ButtonLink to="/new">Report a problem instead</ButtonLink>}
          >
            {q || category ? 'Try different words, or report the problem to IT.' : null}
          </EmptyState>
        </Card>
      )}
      {query.data && query.data.items.length > 0 && (
        <Card className="divide-y divide-line overflow-hidden">
          {query.data.items.map((a) => (
            <Link
              key={a.id}
              to={`/help/${a.id}`}
              className="flex items-center gap-3 px-5 py-4 hover:bg-surface-2"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{a.title}</p>
                <p className="mt-0.5 line-clamp-1 text-sm text-muted">
                  {a.category && `${CATEGORY_LABELS[a.category]} · `}
                  {a.body}
                </p>
              </div>
              {!a.published && (
                <span className="rounded-full bg-warning-soft px-2 py-0.5 text-xs font-semibold text-warning">
                  Draft
                </span>
              )}
              <ChevronRight className="size-5 shrink-0 text-muted" aria-hidden />
            </Link>
          ))}
        </Card>
      )}
    </div>
  );
}

export function ArticlePage() {
  const { id = '' } = useParams();
  const { isIt } = useSession();
  const query = useQuery({
    queryKey: ['kb', 'article', id],
    queryFn: () => api.get<KbArticle>(`/api/kb/${encodeURIComponent(id)}`),
    retry: false,
  });
  usePageTitle(query.data?.title ?? (isIt ? 'Knowledge base' : 'Help articles'));

  if (query.isPending) return <Spinner />;
  if (query.error instanceof ApiError && (query.error.status === 404 || query.error.status === 400)) {
    return <NotFoundPage what="article" />;
  }
  if (query.isError) return <Alert tone="error">{query.error.message}</Alert>;
  const a = query.data;

  return (
    <article className="mx-auto max-w-2xl">
      <div className="mb-2">
        <BackButton to="/help" />
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{a.title}</h1>
          <p className="mt-1 text-sm text-muted">
            {a.category && `${CATEGORY_LABELS[a.category]} · `}Updated {formatDate(a.updatedAt)}
            {!a.published && ' · Draft (only IT can see this)'}
          </p>
        </div>
        {isIt && (
          <ButtonLink to={`/help/${a.id}/edit`} variant="secondary" size="sm">
            <Pencil className="size-4" aria-hidden />
            Edit
          </ButtonLink>
        )}
      </div>
      <Card className="mt-6 p-5 leading-relaxed whitespace-pre-wrap sm:p-6">{a.body}</Card>
      {!isIt && (
        <Card className="mt-6 flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm">Didn’t fix it?</p>
          <ButtonLink to="/new" size="sm">
            Report the problem to IT
          </ButtonLink>
        </Card>
      )}
    </article>
  );
}
